import Big, { Comparison } from 'big.js';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import NextNProgress from 'nextjs-progressbar';
import { useLayoutEffect } from 'react';

import '@ping/assets/css/style.css';
import '@ping/assets/scss/style.scss';

import { setInterceptors } from '@ping/api/interceptors';
import colors from '@ping/assets/scss/theme/exportVariables.module.scss';
import { ENVIRONMENT } from '@ping/configs';
import { TOAST_OPTIONS } from '@ping/constants/toast-options.constant';
import { useForceUpdate } from '@ping/hooks';
import { checkLanguageChange } from '@ping/stores/preferences.effects';
import { preferencesStore } from '@ping/stores/preferences.store';
import {
  forceRedirectFormAUsers,
  forceRedirectNonKycUser,
  forceRedirectSupportUsers,
  forceRedirectUserActivityWaitingForReview,
} from '@ping/stores/userInformation.effects';
import { redirectIncompleteBusinessLogin } from '@ping/pages/business/verification/business-entry-redirect';
import { Toast } from '@ping/uikit';
import MiaxLayout from '@ping/components/Miax/MiaxLayout';
import NasdaqLayout from '@ping/components/Nasdaq/NasdaqLayout';
import { isMiax, isNasdaq, isPingCommodities, isStock, isForex, isExchangeTypeEnabled } from '@ping/utils/checkCompany';
import { useRouter } from 'next/router';

import type { AppProps } from 'next/app';
import { BannersContainer, Layout } from '@ping/components';
import Providers from '@ping/providers';
import initialConfigs from './_base.utils';
import { LOGIN_PATH } from '@ping/authorization/constants';
import { useHandleLocationChangedSideEffect } from '@ping/authorization/side-effects';
import PingCommoditiesLayout from '@ping/components/PingCommodities/PingCommoditiesLayout';

const MIAX_BASE_PATH = '/miax';
const NASDAQ_BASE_PATH = '/nasdaq';
const PING_COMMODITIES = '/commodities';
const BASE_PATHS = {
  MIAX: MIAX_BASE_PATH,
  NASDAQ: NASDAQ_BASE_PATH,
  PING_COMMODITIES: PING_COMMODITIES,
};
// Routes that are allowed even when MIAX mode is active but do **not** start with `/miax`
const MIAX_WHITELIST_PATHS = ['/login/callback', '/user', '/admin', '/trade', '/markets', '/pay', '/campaigns'];

const PingExchangeApp = ({ Component, pageProps }: AppProps) => {
  const forceUpdate = useForceUpdate();
  const router = useRouter();
  const miaxMode = isMiax();
  const nasdaqMode = isNasdaq();
  const pingCommoditiesMode = isPingCommodities();
  const stockMode = isStock();
  const forexMode = isForex();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  /* The CorePass sign-in screen is a standalone surface and renders without the application header. */
  const isHeaderlessRoute = router.pathname === LOGIN_PATH || router.pathname.startsWith('/kyb');
  /* Mock KYB demo — no live API banners / error toasts that would interrupt the presentation. */
  const isKybDemoRoute = router.pathname.startsWith('/kyb');
  const isWhitelistedRoute = MIAX_WHITELIST_PATHS.some(p => currentPath.startsWith(p));
  const isAllowedRoute =
    (miaxMode && currentPath.startsWith(MIAX_BASE_PATH)) ||
    (nasdaqMode && currentPath.startsWith(NASDAQ_BASE_PATH)) ||
    (pingCommoditiesMode && currentPath.startsWith(PING_COMMODITIES)) ||
    isWhitelistedRoute;

  /* this run synchronously before useEffect and we want interceptor
  to run before everything else and once */
  useLayoutEffect(() => {
    // Feature flag guard: reset to crypto if user is in a type that is not enabled
    const company = localStorage.getItem('company');
    if (company && !isExchangeTypeEnabled(company)) {
      localStorage.removeItem('company');
      window.location.href = '/';
      return;
    }

    initialConfigs();
    useHandleLocationChangedSideEffect().then();
    setInterceptors();
    preferencesStore.setDefaultTheme();
    const languageChangeSubscription = checkLanguageChange(forceUpdate);
    const forceRedirectNonKycUserSubscription = forceRedirectNonKycUser();
    const forceRedirectFormAUsersSubscription = forceRedirectFormAUsers();
    const forceRedirectUserActivityPendingSubscription = forceRedirectUserActivityWaitingForReview();
    const forceRedirectSupportUsersSubscription = forceRedirectSupportUsers();
    const redirectIncompleteBusinessLoginSubscription = redirectIncompleteBusinessLogin();

    if (!isAllowedRoute) {
      miaxMode && router.replace(BASE_PATHS.MIAX as any);
      nasdaqMode && router.replace(BASE_PATHS.NASDAQ as any);
      pingCommoditiesMode && router.replace(BASE_PATHS.PING_COMMODITIES as any);
    }

    return () => {
      languageChangeSubscription.unsubscribe();
      forceRedirectNonKycUserSubscription.unsubscribe();
      forceRedirectFormAUsersSubscription.unsubscribe();
      forceRedirectUserActivityPendingSubscription.unsubscribe();
      forceRedirectSupportUsersSubscription.unsubscribe();
      redirectIncompleteBusinessLoginSubscription.unsubscribe();
    };
  }, [miaxMode, nasdaqMode, currentPath, pingCommoditiesMode, stockMode, forexMode]);

  if (miaxMode && isAllowedRoute) {
    return (
      <Providers {...pageProps}>
        <Toast {...TOAST_OPTIONS} />
        <MiaxLayout>
          <Component {...pageProps} />
        </MiaxLayout>
      </Providers>
    );
  }

  if (pingCommoditiesMode && isAllowedRoute) {
    return (
      <Providers {...pageProps}>
        <Toast {...TOAST_OPTIONS} />
        <PingCommoditiesLayout>
          <Component {...pageProps} />
        </PingCommoditiesLayout>
      </Providers>
    );
  }

  if (nasdaqMode && isAllowedRoute) {
    return (
      <Providers {...pageProps}>
        <Toast {...TOAST_OPTIONS} />
        <NasdaqLayout>
          <Component {...pageProps} />
        </NasdaqLayout>
      </Providers>
    );
  }

  return (
    <Providers {...pageProps}>
      {ENVIRONMENT === 'production' && (
        <Head>
          <script
            dangerouslySetInnerHTML={{
              __html: `
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-KDV9V2G3');
`,
            }}
          />
        </Head>
      )}
      <NextNProgress color={colors.Primary} options={{ showSpinner: false }} />
      {!isKybDemoRoute && <Toast {...TOAST_OPTIONS} />}
      {!isKybDemoRoute && <BannersContainer />}
      {isHeaderlessRoute ? (
        <Component {...pageProps} />
      ) : (
        <Layout>
          <Component {...pageProps} />
        </Layout>
      )}
    </Providers>
  );
};

/* render dev also in CSR */
export default dynamic(() => Promise.resolve(PingExchangeApp), {
  ssr: false,
});

// TODO: DO NOT USE THIS FEATURE UNTIL IT'S FINALIZED
//! NOTE: DO NOT MUTATE PROTOTYPE
// --------------------------------------------------------------------------------
// POLYFILL NUMBER
// --------------------------------------------------------------------------------
Number.prototype.plus = function (x) {
  return Big(this).plus(x).toFixed();
};

Number.prototype.minus = function (x) {
  return Big(this).minus(x).toFixed();
};

Number.prototype.times = function (x) {
  return Big(this).times(x).toFixed();
};

Number.prototype.divide = function (x) {
  return Big(this).div(x).toFixed();
};

Number.prototype.module = function (x) {
  return Big(this).mod(x).toFixed();
};

Number.prototype.power = function (exponent) {
  return Big(this).pow(exponent).toFixed();
};

Number.prototype.absolute = function () {
  return Big(this).abs().toFixed();
};

Number.prototype.compare = function (x) {
  return Big(this).cmp(x);
};

Number.prototype.isEqual = function (x) {
  return Big(this).eq(x);
};

Number.prototype.isGreaterThan = function (x) {
  return Big(this).gt(x);
};

Number.prototype.isGreaterThanEqual = function (x) {
  return Big(this).gte(x);
};

Number.prototype.isLessThan = function (x) {
  return Big(this).lt(x);
};

Number.prototype.isLessThanEqual = function (x) {
  return Big(this).lte(x);
};

// TODO
Number.prototype.toPercentage = function () {
  return `${(Math.round(this * 100) * 0.01).toFixed(2)}%`;
};

// TODO
Number.prototype.toScale = function (_scale: number) {
  console.log('🚀 prototype ~ toScale:', this);
  return this;
};

Number.prototype.toNeat = function () {
  return Big(this).toFixed();
};

String.prototype.padStartRemove = function (fillString: string) {
  let result: string = this.padStart(this.length, fillString);

  while (result.startsWith(fillString)) {
    result = this.slice(fillString.length);
  }

  return result;
};

String.prototype.padEndRemove = function (fillString: string) {
  let result: string = this.padEnd(this.length, fillString);

  while (result.endsWith(fillString)) {
    result = this.slice(0, -fillString.length);
  }

  return result;
};

declare global {
  interface Number {
    plus(x: number | string): string;
    minus(x: number | string): string;
    times(x: number | string): string;
    divide(x: number | string): string;
    module(x: number | string): string;
    power(exponent: number): string;
    absolute(): string;
    compare(x: number | string): Comparison;
    isEqual(x: number | string): boolean;
    isGreaterThan(x: number | string): boolean;
    isGreaterThanEqual(x: number | string): boolean;
    isLessThan(x: number | string): boolean;
    isLessThanEqual(x: number | string): boolean;
    toScale(scale: number): string;
    toPercentage(): string;
    toNeat(): string;
  }

  interface String {
    /**
     * Removes the specified fill string from the start of the string until it no longer starts with the fill string.
     *
     * @param {string} fillString - The string to remove from the start of the string.
     * @return {string} The modified string with the fill string removed from the start.
     */
    padStartRemove(fillString: string): string;
    /**
     * Removes the specified fill string from the end of the string until it no longer ends with the fill string.
     *
     * @param {string} fillString - The string to remove from the end of the string.
     * @return {string} The modified string with the fill string removed from the end.
     */
    padEndRemove(fillString: string): string;
  }
}
